import { useState, useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { getApiUrl } from "@/utils/api";
import { PAGE_SIZE } from "@/store";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Pencil, Trash, ArrowUpDown, Users, Eraser } from "lucide-react";
import UsersForm from "./UsersForm";
import { DeleteModal } from "@/components/delete-modal";
import { Pagination } from "@/components/pagination";
import { PageHeader } from "@/components/PageHeader";
import { Company, Facility } from "@/types";
import UsersIcon from "@/components/icons/UsersIcon";

interface UserListItem {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  rol: "admin" | "client" | "facility";
  company: Company;
  facility: Facility | null;
  date_joined: string;
}

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserListItem[];
}

export default function UsersList() {
  const { t } = useTranslation();
  const { token, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserListItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  const ordering = useMemo(() => {
    if (sorting.length === 0) return "id";
    const { id, desc } = sorting[0];
    return `${desc ? "-" : ""}${id}`;
  }, [sorting]);

  const debouncedSetName = useMemo(
    () =>
      debounce((value) => {
        setCurrentPage(1);
        setName(value);
      }, 300),
    [],
  );

  const debouncedSetEmail = useMemo(
    () =>
      debounce((value) => {
        setCurrentPage(1);
        setEmail(value);
      }, 300),
    [],
  );

  const { data, isLoading, error } = useQuery<ApiResponse>({
    queryKey: ["users", currentPage, name, email, ordering],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: PAGE_SIZE.toString(),
      });
      if (name) params.append("name", name);
      if (email) params.append("email", email);
      if (ordering) params.append("ordering", ordering);

      const response = await fetch(getApiUrl(`/users/?${params.toString()}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      return response.json();
    },
    enabled: !!token,
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await fetch(getApiUrl(`users/${userId}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(t("users.deleteError"));
      }
    },
    onSuccess: () => {
      toast.success(t("users.deleteSuccess"));
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setUserToDelete(null);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleEdit = (userId: number) => {
    setEditingUserId(userId);
    setIsDrawerOpen(true);
  };

  const handleNew = () => {
    setEditingUserId(null);
    setIsDrawerOpen(true);
  };

  const handleDeleteClick = (user: UserListItem) => {
    if (currentUser && user.id === currentUser.id) {
      toast.error(t("users.deleteCurrentUserError"));
      return;
    }
    setUserToDelete(user);
  };

  const handleSort = (id: string) => {
    setSorting((prev) => {
      const isCurrent = prev[0]?.id === id;
      if (isCurrent) {
        return [{ id, desc: !prev[0].desc }];
      }
      return [{ id, desc: false }];
    });
  };

  const handleClearFilters = () => {
    const nameInput = document.getElementById(
      "name-filter",
    ) as HTMLInputElement;
    const emailInput = document.getElementById(
      "email-filter",
    ) as HTMLInputElement;
    if (nameInput) nameInput.value = "";
    if (emailInput) emailInput.value = "";
    setName("");
    setEmail("");
    setCurrentPage(1);
  };

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  if (error) {
    return <div className="text-destructive">Error: {error.message}</div>;
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        icon={
          <UsersIcon width={180} height={180} className="w-[50px] h-[50px]" />
        }
        actionTitle="users.add"
        title="users.title"
        description="users.description"
        handleNew={handleNew}
      />

      <div className="flex items-center space-x-2 p-4">
        <Input
          id="name-filter"
          placeholder={t("filters.filterByName")}
          onChange={(e) => debouncedSetName(e.target.value)}
          className="max-w-sm"
        />
        <Input
          id="email-filter"
          placeholder={t("filters.filterByEmail")}
          onChange={(e) => debouncedSetEmail(e.target.value)}
          className="max-w-sm"
        />
        <div
          role="button"
          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
          onClick={handleClearFilters}
        >
          <Eraser size={30} fill="#000000" stroke="#ffffff" strokeWidth={1} />
        </div>
      </div>

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    onClick={() => handleSort("first_name")}
                  >
                    {t("users.fields.name")}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button variant="ghost" onClick={() => handleSort("email")}>
                    {t("users.fields.email")}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>{t("users.fields.rol")}</TableHead>
                <TableHead>{t("users.fields.company")}</TableHead>
                <TableHead className="text-right">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: PAGE_SIZE }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                    </TableRow>
                  ))
                : data?.results.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell className="text-center">
                        {user.email}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="default"
                          className="text-xs bg-primary-foreground text-white"
                        >
                          {t(`${user.rol}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {user.company?.name}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-center items-center">
                          <div
                            role="button"
                            className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                            onClick={() => handleEdit(item.id)}
                          >
                            <Pencil
                              size={30}
                              fill="#000000"
                              stroke="#ffffff"
                              strokeWidth={1}
                            />
                          </div>
                          {currentUser?.id !== user.id ? (
                            <div
                              role="button"
                              className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <Trash
                                size={30}
                                fill="#ff0000"
                                stroke="#ffffff"
                                strokeWidth={2}
                              />
                            </div>
                          ) : (
                            <div />
                          )}
                        </div>
                        {/*<Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(user.id)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(user)}
                          disabled={currentUser?.id === user.id}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>*/}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
        {data && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            hasNextPage={!!data?.next}
            hasPreviousPage={!!data?.previous}
          />
        )}
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingUserId ? t("users.edit") : t("users.add")}
            </DrawerTitle>
            <DrawerDescription>
              {editingUserId
                ? t("users.edit-description")
                : t("users.add-description")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4">
            <UsersForm
              userId={editingUserId}
              onSuccess={() => {
                setIsDrawerOpen(false);
                queryClient.invalidateQueries({ queryKey: ["users"] });
              }}
              onCancel={() => setIsDrawerOpen(false)}
            />
          </div>
        </DrawerContent>
      </Drawer>
      {userToDelete && (
        <DeleteModal
          open={!!userToDelete}
          onOpenChange={(open) => !open && setUserToDelete(null)}
          onDelete={() => deleteUserMutation.mutate(userToDelete.id)}
          title={t("users.deleteTitle")}
          entity={`${userToDelete.first_name} ${userToDelete.last_name}`}
          isPending={deleteUserMutation.isPending}
        />
      )}
    </div>
  );
}
